import { configureStore } from "@reduxjs/toolkit";
import gameReducer from "./game/gameSlice";

const SCRATCHPAD_KEY = "scratchpad";
const SAVED_DESIGNS_KEY = "savedDesigns";
const CURRENT_DESIGN_KEY = "currentDesignName";
const LEGACY_KEY = "gameState"; // old key — migrated on first load

// ---------------------------------------------------------------------------
// Design-management helpers (exported for UI components)
// ---------------------------------------------------------------------------

export const getCurrentDesignName = (): string | null => {
  try {
    return localStorage.getItem(CURRENT_DESIGN_KEY) || null;
  } catch {
    return null;
  }
};

export const setCurrentDesignName = (name: string | null) => {
  try {
    if (name === null) {
      localStorage.removeItem(CURRENT_DESIGN_KEY);
    } else {
      localStorage.setItem(CURRENT_DESIGN_KEY, name);
    }
  } catch {
    // ignore
  }
};

const getSavedDesignsMap = (): Record<string, any> => {
  try {
    const raw = localStorage.getItem(SAVED_DESIGNS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const setSavedDesignsMap = (map: Record<string, any>) => {
  try {
    localStorage.setItem(SAVED_DESIGNS_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
};

/** Return sorted list of saved design names. */
export const getSavedDesigns = (): string[] => {
  return Object.keys(getSavedDesignsMap()).sort();
};

/** Snapshot the current Redux state into savedDesigns[name]. */
export const saveDesign = (name: string) => {
  const state = store.getState().game;
  const map = getSavedDesignsMap();
  map[name] = state;
  setSavedDesignsMap(map);
  setCurrentDesignName(name);
};

/** Retrieve a saved design's state. */
export const loadDesign = (name: string): any | undefined => {
  return getSavedDesignsMap()[name];
};

/** Delete a saved design from localStorage. */
export const deleteDesign = (name: string) => {
  const map = getSavedDesignsMap();
  delete map[name];
  setSavedDesignsMap(map);
  // If we just deleted the currently-loaded design, fall back to scratchpad
  if (getCurrentDesignName() === name) {
    setCurrentDesignName(null);
  }
};

// ---------------------------------------------------------------------------
// State load / save
// ---------------------------------------------------------------------------

const loadState = () => {
  try {
    // Migrate legacy key → scratchpad (one-time)
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy !== null) {
      localStorage.setItem(SCRATCHPAD_KEY, legacy);
      localStorage.removeItem(LEGACY_KEY);
    }

    const designName = getCurrentDesignName();
    if (designName) {
      const design = loadDesign(designName);
      if (design) return design;
    }

    const serializedState = localStorage.getItem(SCRATCHPAD_KEY);
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    return undefined;
  }
};

const saveState = (state: any) => {
  try {
    const serializedState = JSON.stringify(state);
    const designName = getCurrentDesignName();
    if (designName) {
      // Persist into the named design slot
      const map = getSavedDesignsMap();
      map[designName] = state;
      setSavedDesignsMap(map);
    } else {
      // Persist into scratchpad
      localStorage.setItem(SCRATCHPAD_KEY, serializedState);
    }
  } catch {
    // ignore write errors
  }
};

const preloadedState = loadState();

export const store = configureStore({
  reducer: {
    game: gameReducer,
  },
  preloadedState: preloadedState ? { game: preloadedState } : undefined,
});

store.subscribe(() => {
  saveState(store.getState().game);
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
